/**
 * 3D Visualizer — Framing Settings
 *
 * Tiny pub-sub for dev-tunable framing parameters. The LightingPanel writes
 * here; Room3DViewer's renderScene reads here. Lives outside React state so
 * adjustments don't trigger React re-renders or scene rebuilds — the viewer
 * just calls renderScene() after a setting change.
 */

import * as THREE from 'three';

interface FramingSettings {
  /**
   * Intensity multiplier for the framing fade gradient.
   * Scales every non-foreground wall's opacity. 1.0 = use the full
   * dot-product value; lower values fade everything proportionally.
   */
  nearestOpacity: number;

  /**
   * Hard ceiling on framing opacity. The most camera-facing wall is
   * always 0; every other wall's opacity is clamped to this maximum
   * after the intensity multiplier is applied.
   * Default 1.0 (no ceiling); 0.5 = no stud ever exceeds 50% opacity.
   */
  maxOpacity: number;
}

let settings: FramingSettings = {
  nearestOpacity: 1.0,
  maxOpacity: 1.0,
};

const listeners: Set<(s: FramingSettings) => void> = new Set();

export function getFramingSettings(): FramingSettings {
  return settings;
}

export function setFramingNearestOpacity(value: number): void {
  const clamped = Math.max(0, Math.min(1, value));
  if (clamped === settings.nearestOpacity) return;
  settings = { ...settings, nearestOpacity: clamped };
  listeners.forEach((fn) => fn(settings));
}

export function setFramingMaxOpacity(value: number): void {
  const clamped = Math.max(0, Math.min(1, value));
  if (clamped === settings.maxOpacity) return;
  settings = { ...settings, maxOpacity: clamped };
  listeners.forEach((fn) => fn(settings));
}

export function onFramingSettingsChange(fn: (s: FramingSettings) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Apply an opacity value to every mesh in a framing wall group. Materials
 * are per-face (cloned in buildRoomFraming), so this only affects this
 * wall's framing.
 */
export function applyFramingOpacity(group: THREE.Object3D, opacity: number): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mat = child.material;
      if (mat instanceof THREE.MeshStandardMaterial) {
        mat.transparent = opacity < 1;
        mat.opacity = opacity;
        mat.depthWrite = opacity > 0.9;
      }
    }
  });
}
